/**
 * RetentionEngagementPanel (US-17-GR-24)
 *
 * Displays Week-1 / Week-2 / Week-4 retention averages plus engagement KPIs
 * (WAU, tasks/active-user, 3-day-streak maintenance, Easy Button adoption)
 * with PRD target lines. Data comes from adminMetricsService — no mocked data.
 *
 * Recovered from the deleted user-app panel (frontend commit 9c418bd3f^
 * src/components/agent/admin/RetentionEngagementPanel.tsx) and re-homed into
 * ChellessAdmin conventions.
 *
 * RetentionMetrics (RetentionSeriesResponse):
 *   Retention data arrives as parallel arrays: dates[], weekOneRetention[],
 *   weekTwoRetention[], weekFourRetention[]. Averages are computed client-side
 *   via averageFromSeries() (null entries are excluded).
 *
 * Engagement KPIs are NESTED under AdminMetricsSummary.engagement.
 *
 * The streak card reads AdminMetricsSummary.streaks, the same block the mobile
 * AdminPage reads, so both surfaces show one definition (DEC-2026-10-06). No
 * streak target is set, and the coverage line says how much history it covers.
 */

import { useEffect, useState, useCallback, startTransition, type ReactNode } from 'react';
import { Users, TrendingUp, Zap, RefreshCw, AlertCircle } from 'lucide-react';
import type { AdminMetricsSummary, RetentionMetrics, MetricWindow } from '../services/adminMetricsService';
import { getAdminMetrics, getRetentionSeries, averageFromSeries } from '../services/adminMetricsService';
import { Sparkline } from './Sparkline';
import { useLatestRequest } from '../hooks/useLatestRequest';

/**
 * Plain statement of the ratified definitions (DEC-2026-09-08 Rulings A, C, D) at the point of use.
 * Replaces the earlier UsageEvent wording, which never described what the API computed.
 */
function RetentionScopeDisclosure() {
  return (
    <div
      role="note"
      aria-label="Retention and adoption definitions"
      className="p-3 bg-zinc-800/50 border border-zinc-700 rounded-lg text-xs text-zinc-300 space-y-1.5"
    >
      <p>
        <strong className="text-zinc-100">Retention</strong> groups everyone who created an account in a
        calendar week (ISO week, Monday 00:00 UTC). Someone is retained at week N if they took at least one
        qualifying action on or after the start of week N. A week with no signups shows{' '}
        <strong className="text-zinc-100">No data</strong>, never 0%.
      </p>
      <p>
        <strong className="text-zinc-100">Qualifying actions</strong> are an AI generation, a board card state
        change, or an agent run that produced an output. Signing in, being seen by the server, and creating an
        account do not count. An account is active if it took at least one qualifying action in the last 7
        days.
      </p>
      <p>
        <strong className="text-zinc-100">Easy Button adoption</strong> is the number of accounts that used
        the Easy Button in the last 7 days, out of active accounts in the same window, as a percent from 0 to
        100.
      </p>
      <p>
        Each week-N value is re-measured daily and covers the days of week N elapsed so far, so early-week
        readings cover fewer days. Degraded (fallback) agent runs count as activity: an activity signal,
        never a quality signal. Failed runs do not count.
      </p>
      <p>PRD targets are stated for day 1/7/30; no weekly target is ratified yet.</p>
    </div>
  );
}

interface RetentionEngagementPanelProps {
  window: MetricWindow;
}

function pct(n: number | null): string {
  if (n === null) return 'No data';
  return `${n.toFixed(1)}%`;
}

/** Drop null snapshots, preserving chronological order, for Sparkline (which takes number[]). */
function nonNullSeries(series: (number | null)[]): number[] {
  return series.filter((v): v is number => v !== null);
}

function KpiCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="bg-zinc-800/50 rounded-lg p-4">
      <p className="text-2xl font-semibold text-zinc-100">{value}</p>
      <p className="text-xs text-zinc-400 mt-1">{label}</p>
      {sub && <p className="text-xs text-zinc-600 mt-0.5">{sub}</p>}
    </div>
  );
}

/** Retention KPI card variant with a trend sparkline (US-17-GR-28). */
function RetentionKpiCard({
  label,
  value,
  sub,
  series,
  color,
}: {
  label: string;
  value: string;
  sub?: string;
  series: (number | null)[];
  color: string;
}) {
  const values = nonNullSeries(series);
  return (
    <div className="bg-zinc-800/50 rounded-lg p-4">
      <p className="text-2xl font-semibold text-zinc-100">{value}</p>
      <p className="text-xs text-zinc-400 mt-1">{label}</p>
      {sub && <p className="text-xs text-zinc-600 mt-0.5">{sub}</p>}
      <div className="mt-2">
        <Sparkline series={[{ values, color, label }]} width={140} height={36} />
      </div>
    </div>
  );
}

/**
 * "Estimated" disclosure badge (US-17-GR-37). Not colour-only — carries a
 * text label plus a title/aria-label explaining that the figure is a proxy,
 * not a measurement, so an admin reading the card sees the caveat inline.
 */
function EstimateBadge({ explanation }: { explanation: string }) {
  return (
    <span
      title={explanation}
      aria-label={`Estimated: ${explanation}`}
      tabIndex={0}
      className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded border border-amber-500/30 bg-amber-500/10 text-[10px] font-medium text-amber-300 cursor-help"
    >
      Estimated
    </span>
  );
}

/** Simpler KPI card variant used for engagement row */
function EngagementKpiCard({
  label,
  value,
  sub,
  icon,
  isEstimate,
  estimateExplanation,
}: {
  label: string;
  value: string;
  sub?: string;
  icon?: ReactNode;
  isEstimate?: boolean;
  estimateExplanation?: string;
}) {
  return (
    <div className="bg-zinc-800/50 rounded-lg p-4">
      <p className="text-2xl font-semibold text-zinc-100 flex items-center flex-wrap">
        {value}
        {isEstimate && <EstimateBadge explanation={estimateExplanation ?? 'This figure is an estimate.'} />}
      </p>
      <p className="text-xs text-zinc-400 mt-1 flex items-center gap-1">
        {icon}
        {label}
      </p>
      {sub && <p className="text-xs text-zinc-600 mt-0.5">{sub}</p>}
    </div>
  );
}

export function RetentionEngagementPanel({ window }: RetentionEngagementPanelProps) {
  const [summary, setSummary] = useState<AdminMetricsSummary | null>(null);
  const [retention, setRetention] = useState<RetentionMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const beginLoad = useLatestRequest();

  const load = useCallback(async () => {
    const isCurrent = beginLoad();
    setLoading(true);
    setError(null);
    try {
      const [s, r] = await Promise.all([
        getAdminMetrics(window),
        getRetentionSeries(window),
      ]);
      if (!isCurrent()) return;
      setSummary(s);
      setRetention(r);
    } catch (err) {
      if (!isCurrent()) return;
      setError(err instanceof Error ? err.message : 'Failed to load retention data');
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [window, beginLoad]);

  useEffect(() => {
    startTransition(() => { void load(); });
  }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <RefreshCw className="w-5 h-5 text-zinc-500 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
        <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
        <div>
          <p className="text-sm text-red-400 font-medium">Failed to load retention metrics</p>
          <p className="text-xs text-zinc-500 mt-0.5">{error}</p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          className="ml-auto px-3 py-1.5 text-xs rounded-lg bg-zinc-800 text-zinc-300 hover:bg-zinc-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  // Compute averages client-side from parallel retention arrays.
  // averageFromSeries() skips null entries; returns null when all are null.
  const avgWeek1 = retention ? averageFromSeries(retention.weekOneRetention ?? []) : null;
  const avgWeek2 = retention ? averageFromSeries(retention.weekTwoRetention ?? []) : null;
  const avgWeek4 = retention ? averageFromSeries(retention.weekFourRetention ?? []) : null;

  return (
    <div className="space-y-4">
      {/* Section header */}
      <div className="flex items-center gap-2">
        <TrendingUp className="w-4 h-4 text-violet-400" />
        <h3 className="text-sm font-medium text-zinc-200">Retention &amp; Engagement</h3>
      </div>

      <RetentionScopeDisclosure />

      {/* Retention averages computed from parallel arrays */}
      {retention && (
        <div className="grid grid-cols-3 gap-3">
          <RetentionKpiCard
            label="Week 1 Retention (avg)"
            value={pct(avgWeek1)}
            sub={`last ${window} days`}
            series={retention.weekOneRetention ?? []}
            color="#a78bfa"
          />
          <RetentionKpiCard
            label="Week 2 Retention (avg)"
            value={pct(avgWeek2)}
            sub={`last ${window} days`}
            series={retention.weekTwoRetention ?? []}
            color="#34d399"
          />
          <RetentionKpiCard
            label="Week 4 Retention (avg)"
            value={pct(avgWeek4)}
            sub={`last ${window} days`}
            series={retention.weekFourRetention ?? []}
            color="#fbbf24"
          />
        </div>
      )}

      {/* Engagement KPIs — read from nested summary.engagement */}
      {summary?.engagement && (
        <>
          <div className="flex items-center gap-2 mt-2">
            <Users className="w-4 h-4 text-blue-400" />
            <h4 className="text-xs font-medium text-zinc-400">Engagement KPIs</h4>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <KpiCard
              label="Weekly Active Users"
              value={summary.engagement.wau.toLocaleString()}
              sub={`target ≥${summary.engagement.wauTarget.toFixed(0)}% of users`}
            />
            <KpiCard
              label="Tasks / Active User / Wk"
              value={summary.engagement.tasksPerActiveUserPerWeek.toFixed(1)}
            />
            <KpiCard
              label="3+ day streak rate"
              value={`${(summary.streaks?.threePlusStreakRate ?? summary.engagement.threeDayStreakMaintenance).toFixed(1)}%`}
              sub={streakSub(summary)}
            />
            <EngagementKpiCard
              label="Easy Button Adoption"
              value={adoptionValue(summary.engagement)}
              sub={adoptionSub(summary.engagement)}
              icon={<Zap className="w-3 h-3 text-violet-400" />}
              isEstimate={summary.engagement.easyButtonAdoptionIsEstimate}
              estimateExplanation="Proxy estimate: users with 5+ completed tasks, not a measured Easy Button usage count. The real generation records could not be read."
            />
          </div>
          {summary.streaks?.coverageLine && (
            <p className="text-xs text-zinc-500" data-testid="streak-coverage">
              Streaks: {summary.streaks.coverageLine}
            </p>
          )}
        </>
      )}
    </div>
  );
}

type EngagementBlock = AdminMetricsSummary['engagement'];

function adoptionValue(e: EngagementBlock): string {
  if (e.easyButtonAdoptionFault === true) return 'Out of range, not shown';
  const v = e.easyButtonAdoption;
  return v == null ? 'No data' : `${v.toFixed(1)}%`;
}

function adoptionSub(e: EngagementBlock): string {
  const target = `target ${e.easyButtonTarget.toFixed(0)}%`;
  if (e.easyButtonAdoptionFault === true || e.easyButtonAdoption == null) return target;
  const n = e.easyButtonAdoptionNumerator;
  const m = e.easyButtonAdoptionDenominator;
  return n == null || m == null ? target : `${n} of ${m} active accounts · ${target}`;
}

function streakSub(summary: AdminMetricsSummary): string {
  const target = summary.streaks?.targetRate ?? summary.engagement.streakTarget;
  const targetText = target == null ? 'No target set' : `target ${target.toFixed(0)}%`;
  const denominator = summary.streaks?.denominatorLabel;
  return denominator ? `${denominator} · ${targetText}` : targetText;
}

export default RetentionEngagementPanel;
